import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Spinner } from '../../ui';
import { apiErrorMessage, formatCountdown } from './status';
import type { ShipNodeStationsService } from './types';

type Pairing =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; code: string; expiresAtMs: number };

type AddStationModalProps = {
  service: Pick<ShipNodeStationsService, 'createPairingCode'>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

/**
 * Creates a one-time pairing code and shows it with its countdown. The code
 * lives only in this component's state: it's a credential for the station.
 */
export function AddStationModal({
  service,
  isOpen,
  onOpenChange,
}: AddStationModalProps) {
  const [pairing, setPairing] = useState<Pairing>({ status: 'loading' });
  const [now, setNow] = useState(() => Date.now());

  const generate = useCallback(async () => {
    setPairing({ status: 'loading' });
    try {
      const { code, expiresInSeconds } = await service.createPairingCode();
      /* From our own clock, so a skewed PC clock can't shorten or stretch it. */
      const startedAt = Date.now();
      setNow(startedAt);
      setPairing({
        status: 'ready',
        code,
        expiresAtMs: startedAt + expiresInSeconds * 1000,
      });
    } catch (err) {
      setPairing({
        status: 'error',
        message: apiErrorMessage(err, 'Could not create a pairing code'),
      });
    }
  }, [service]);

  useEffect(() => {
    if (isOpen) {
      generate();
    } else {
      setPairing({ status: 'loading' });
    }
  }, [isOpen, generate]);

  const isCounting = pairing.status === 'ready' && now < pairing.expiresAtMs;

  useEffect(() => {
    if (!isCounting) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isCounting]);

  const secondsLeft =
    pairing.status === 'ready' ? (pairing.expiresAtMs - now) / 1000 : 0;
  const isExpired = pairing.status === 'ready' && secondsLeft <= 0;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Add ShipNode station"
      size="md"
      footer={
        <>
          {isExpired || pairing.status === 'error' ? (
            <Button variant="primary" onPress={generate}>
              Generate new code
            </Button>
          ) : null}
          <Button onPress={() => onOpenChange(false)}>Done</Button>
        </>
      }
    >
      <div className="sk:flex sk:flex-col sk:items-center sk:gap-4 sk:py-2 sk:text-center">
        {pairing.status === 'loading' ? (
          <Spinner label="Creating pairing code" />
        ) : null}

        {pairing.status === 'error' ? (
          <p role="alert" className="sk:m-0 sk:text-danger">
            {pairing.message}
          </p>
        ) : null}

        {pairing.status === 'ready' ? (
          <>
            <p className="sk:m-0 sk:text-sm sk:text-fg">
              Open ShipNode on the packing PC and type this code
            </p>
            <output
              aria-label="Pairing code"
              className={
                isExpired
                  ? 'sk:font-mono sk:text-4xl sk:font-semibold sk:tracking-widest sk:text-fg-tertiary sk:line-through'
                  : 'sk:font-mono sk:text-4xl sk:font-semibold sk:tracking-widest sk:text-fg sk:select-all'
              }
            >
              {pairing.code}
            </output>
            {/* Only the expiry is announced; a live region ticking every second would drown a screen reader. */}
            {isExpired ? (
              <p
                role="status"
                className="sk:m-0 sk:text-dense sk:text-fg-secondary"
              >
                This code expired.
              </p>
            ) : (
              <p className="sk:m-0 sk:text-dense sk:text-fg-secondary sk:tabular-nums">
                Expires in {formatCountdown(secondsLeft)}
              </p>
            )}
          </>
        ) : null}
      </div>
    </Modal>
  );
}
